// ─────────────────────────────────────────────────────────────────────────
// AsistenciaCard — asistencia v1 INFORMATIVA de un trabajador (27-ago-2026).
// Componente standalone: recibe `trabajadorId` y se conecta solo al backend
// (/asistencia). Grilla del mes con carga MANUAL de entrada/salida por día.
//
// ⚠️ NO reemplaza el libro de asistencia legal: el libro oficial sigue EN
// PAPEL hasta que la Dirección del Trabajo autorice el marcaje electrónico
// (trámite en evaluación). Esto es solo apoyo de gestión interna.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import backend from "../../backend";

const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "6px 8px", fontSize: 13, color: "#ddd", width: 92, boxSizing: "border-box", ...num },
  nav:   { minHeight: 32, minWidth: 32, borderRadius: 8, border: "1px solid #333", background: "#1e1e1e", color: "#b8b8b8", fontSize: 13, cursor: "pointer" },
};

const DOW = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

const hoyISO = () => {
  const d = new Date();
  const p = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const AsistenciaCard = ({ trabajadorId }) => {
  // Mes visible "AAAA-MM" (parte en el mes actual)
  const [mes, setMes] = useState(hoyISO().slice(0, 7));
  const [regs, setRegs] = useState({});       // "AAAA-MM-DD" → registro guardado
  const [edits, setEdits] = useState({});     // ediciones locales sin guardar
  const [cargando, setCargando] = useState(true);
  const [guardandoDia, setGuardandoDia] = useState(null);
  const [aviso, setAviso] = useState(null);

  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); setTimeout(() => setAviso(null), 4000); };

  const cargar = useCallback(async () => {
    if (!trabajadorId) return;
    setCargando(true);
    try {
      const r = await backend.get(`asistencia?trabajador=${trabajadorId}&mes=${mes}`);
      const map = {};
      (r.data || []).forEach(reg => { map[String(reg.fecha).slice(0, 10)] = reg; });
      setRegs(map);
      setEdits({});
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [trabajadorId, mes]);
  useEffect(() => { cargar(); }, [cargar]);

  const moverMes = delta => {
    const [a, m] = mes.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1 + delta, 1));
    setMes(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
  };

  const setEdit = (dia, campo, valor) => {
    setEdits(prev => ({
      ...prev,
      [dia]: {
        horaEntrada: regs[dia]?.horaEntrada || "",
        horaSalida:  regs[dia]?.horaSalida || "",
        ...prev[dia],
        [campo]: valor,
      },
    }));
  };

  const guardarDia = async dia => {
    const e = edits[dia];
    if (!e) return;
    setGuardandoDia(dia);
    try {
      const r = await backend.post("asistencia", {
        trabajador: trabajadorId,
        fecha: dia,
        horaEntrada: e.horaEntrada || "",
        horaSalida: e.horaSalida || "",
      });
      setRegs(prev => ({ ...prev, [dia]: r.data }));
      setEdits(prev => { const n = { ...prev }; delete n[dia]; return n; });
      avisar("ok", `Día ${dia.slice(8, 10)} guardado`);
    } catch (err) { avisar("error", err?.response?.data?.error || err.message); }
    setGuardandoDia(null);
  };

  if (!trabajadorId) return null;

  const [anio, mesNum] = mes.split("-").map(Number);
  const diasEnMes = new Date(Date.UTC(anio, mesNum, 0)).getUTCDate();
  const hoy = hoyISO();

  return (
    <div style={S.card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>🕐 Asistencia (informativa)</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button onClick={() => moverMes(-1)} className="t-02" style={S.nav}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#ddd", minWidth: 130, textAlign: "center" }}>
            {MESES[mesNum - 1]} {anio}
          </span>
          <button onClick={() => moverMes(1)} className="t-02" style={S.nav}>›</button>
        </div>
      </div>

      {cargando ? (
        <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 420 }}>
            <thead>
              <tr>
                {["Día", "Entrada", "Salida", "Obs.", ""].map((h, i) => (
                  <th key={i} style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em", textAlign: "left", padding: "4px 8px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: diasEnMes }, (_, i) => {
                const dd = String(i + 1).padStart(2, "0");
                const dia = `${mes}-${dd}`;
                const dow = new Date(`${dia}T00:00:00Z`).getUTCDay();
                const finde = dow === 0 || dow === 6;
                const reg = regs[dia];
                const edit = edits[dia];
                const entrada = edit ? edit.horaEntrada : (reg?.horaEntrada || "");
                const salida = edit ? edit.horaSalida : (reg?.horaSalida || "");
                const esHoy = dia === hoy;
                return (
                  <tr key={dia} style={{ borderTop: "1px solid #2a2a2a", background: esHoy ? "#26202066" : "transparent", opacity: finde ? 0.45 : 1 }}>
                    <td style={{ padding: "5px 8px", fontSize: 12, color: esHoy ? "#fff" : "#b8b8b8", whiteSpace: "nowrap", ...num }}>
                      <span style={{ fontWeight: esHoy ? 800 : 400 }}>{dd}</span>
                      <span style={{ color: "#8a8a8a", fontSize: 10 }}> {DOW[dow]}</span>
                    </td>
                    <td style={{ padding: "3px 8px" }}>
                      <input type="time" style={S.input} value={entrada} onChange={e => setEdit(dia, "horaEntrada", e.target.value)} />
                    </td>
                    <td style={{ padding: "3px 8px" }}>
                      <input type="time" style={S.input} value={salida} onChange={e => setEdit(dia, "horaSalida", e.target.value)} />
                    </td>
                    <td style={{ padding: "3px 8px", fontSize: 11, color: "#8a8a8a", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {reg?.observacion || ""}
                    </td>
                    <td style={{ padding: "3px 8px", textAlign: "right" }}>
                      {edit && (
                        <button onClick={() => guardarDia(dia)} disabled={guardandoDia === dia} className="t-02"
                          style={{ minHeight: 30, borderRadius: 8, border: "none", background: guardandoDia === dia ? "#333" : "#e30613", color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "0 12px" }}>
                          {guardandoDia === dia ? "…" : "Guardar"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 10, borderTop: "1px solid #2a2a2a", paddingTop: 8 }}>
        ⚠ Registro informativo de gestión interna — <b style={{ color: "#b8b8b8" }}>el libro de asistencia legal sigue en papel</b> hasta la autorización de la Dirección del Trabajo (marcaje electrónico en evaluación).
      </div>
    </div>
  );
};

export default AsistenciaCard;
